import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import './ActiveReceivingPage.css';
import ActiveReceivingHeader from './components/ActiveReceivingHeader';
import ReceivingStatusTabs from './components/ReceivingStatusTabs';
import ReceivingTradeCard from './components/ReceivingTradeCard';
import { useGetActiveTradesQuery } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import MagicButton from 'components/MagicButton/MagicButton';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';

export type ReceivingStatus = 'preparing' | 'transit' | 'completed' | 'cancelled';

export interface ReceivingTradeItem {
  qty: number;
  name: string;
  set: string;
  setCode?: string;
  setIconUrl?: string;
  condition: string;
  language: string;
  image: string;
  pointValue?: number;
  foil?: boolean;
}

export interface ReceivingTrade {
  id: string;
  tradeNumber: string;
  traderId: string;
  traderName: string;
  traderAvatar: string;
  pointBalance?: number;
  initiated: string;
  shipped?: string;
  completed?: string;
  cancelled?: string;
  estimatedDelivery?: string;
  status: ReceivingStatus;
  items: ReceivingTradeItem[];
  totalValue: number;
  speed: number;
  accuracy: number;
  communication: number;
  shippingAddress: {
    name: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  cancelledRaw?: string;
  isRemovedItem?: boolean;
  removedFromTradeNumber?: string;
}

export default function ActiveReceivingPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReceivingStatus>('preparing');
  const [retryCount, setRetryCount] = useState(0);
  const [skipQuery, setSkipQuery] = useState(false);
  const hasSetInitialTab = useRef(false);

  // Fetch active trades from API
  const { data: tradesData, isLoading, error, refetch } = useGetActiveTradesQuery(
    { type: 'receiving' },
    {
      // Skip the query if we've had too many 401 errors
      skip: skipQuery
    }
  );

  // Convert API trades to component format
  // Format date from ISO string to MM.DD.YYYY
  const formatDate = (dateString: string | undefined): string | undefined => {
    if (!dateString) return undefined;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}.${day}.${year}`;
    } catch {
      return dateString;
    }
  };

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => (tradesData?.trades || []).map(t => t.traderId),
    [tradesData?.trades]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  const trades: ReceivingTrade[] = tradesData?.trades.map(trade => ({
    id: trade.id,
    tradeNumber: trade.tradeNumber,
    traderId: trade.traderId.toString(),
    traderName: trade.traderName,
    traderAvatar: trade.traderAvatar || '',
    pointBalance: trade.pointBalance,
    initiated: formatDate(trade.initiated) || trade.initiated,
    shipped: formatDate(trade.shipped) || trade.shipped,
    completed: formatDate(trade.completed) || trade.completed,
    cancelled: trade.cancelled ? (formatDate(trade.cancelled) || trade.cancelled) : undefined,
    cancelledRaw: trade.cancelled || undefined,
    estimatedDelivery: trade.estimatedDelivery,
    // Map statuses: ready -> preparing (sender hasn't shipped yet), cancelled stays cancelled
    status: trade.status === 'cancelled' ? 'cancelled' : trade.status === 'ready' ? 'preparing' : trade.status === 'transit' ? 'transit' : 'completed',
    items: trade.items.map(item => ({
      qty: item.qty,
      name: item.name,
      set: item.set,
      setCode: (item as any).setCode,
      setIconUrl: (item as any).setIconUrl,
      condition: item.condition,
      language: item.language,
      image: item.image,
      pointValue: item.pointValue || 0,
      foil: item.foil || false,
    })),
    totalValue: trade.totalValue,
    speed: trade.speed ?? 5,  // Rating from backend, default to 5
    accuracy: trade.accuracy ?? 5,  // Rating from backend, default to 5
    communication: trade.communication ?? 5,  // Rating from backend, default to 5
    shippingAddress: trade.shippingAddress ? {
      name: `${trade.shippingAddress.firstName || ''} ${trade.shippingAddress.lastName || ''}`.trim() || 'Not provided',
      street: trade.shippingAddress.address1 || '',
      city: trade.shippingAddress.city || '',
      state: trade.shippingAddress.state || '',
      zipCode: trade.shippingAddress.postalCode || '',
      country: trade.shippingAddress.country || '',
    } : {
      name: 'Not provided',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    isRemovedItem: (trade as any).isRemovedItem || false,
    removedFromTradeNumber: (trade as any).removedFromTradeNumber,
  })) || [];

  // Only use real API data - no mock data
  const filteredTrades = trades.filter(trade => trade.status === activeTab)
    .sort((a, b) => {
      if (activeTab === 'cancelled') {
        return new Date(b.cancelledRaw || '').getTime() - new Date(a.cancelledRaw || '').getTime();
      }
      return 0;
    });

  const summaryStats = {
    preparing: trades.filter(t => t.status === 'preparing').length,
    transit: trades.filter(t => t.status === 'transit').length,
    completed: trades.filter(t => t.status === 'completed').length,
    totalPoints: trades.reduce((sum, t) => sum + t.totalValue, 0)
  };

  // Auto-select the first tab with content on initial load
  useEffect(() => {
    if (tradesData && !hasSetInitialTab.current) {
      hasSetInitialTab.current = true;
      if (summaryStats.preparing === 0 && summaryStats.transit > 0) {
        setActiveTab('transit');
      }
    }
  }, [tradesData, summaryStats.preparing, summaryStats.transit]);

  if (isLoading) {
    return (
      <div className="active-receiving-page">
        <ActiveReceivingHeader stats={{ preparing: 0, transit: 0, completed: 0, totalPoints: 0 }} />
        <ReceivingStatusTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="receiving-trades-container">
          <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
            Loading incoming trades...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorStatus = (error as any).status;
    const errorMessage = (error as any).data?.message || (error as any).data?.error;

    console.error('Failed to fetch receiving trades:', {
      status: errorStatus,
      message: errorMessage,
      fullError: error
    });

    // Special handling for 401 errors
    if (errorStatus === 401) {
      console.warn('⚠️ 401 error on /receiving page - This might be a backend permission issue');

      // Show a user-friendly error message instead of logging out
      return (
        <div className="active-receiving-page">
          <ActiveReceivingHeader stats={{ preparing: 0, transit: 0, completed: 0, totalPoints: 0 }} />
          <ReceivingStatusTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <div className="receiving-trades-container">
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#fff',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h3 style={{ color: '#ff9800', marginBottom: '20px' }}>
                Unable to Load Incoming Trades
              </h3>
              <p style={{ marginBottom: '20px' }}>
                We're having trouble loading your incoming trades.
                Please try again in a moment.
              </p>
              <button
                onClick={() => {
                  const newCount = retryCount + 1;
                  setRetryCount(newCount);
                  if (newCount > 2) {
                    setSkipQuery(true);
                  }
                  refetch();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Try Again
              </button>
              <p style={{ marginTop: '20px', fontSize: '14px', color: '#aaa' }}>
                If this persists, try refreshing the page or logging out and back in.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // For other errors, show a generic error message
  }

  return (
    <div className="active-receiving-page">
      <h1 className="active-receiving-page-title">Active Trades</h1>
      <ActiveReceivingHeader stats={summaryStats} />
      <ReceivingStatusTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="receiving-trades-container">
        {filteredTrades.length > 0 ? (
          filteredTrades.map(trade => (
            <ReceivingTradeCard 
              key={trade.id} 
              trade={trade} 
            />
          ))
        ) : (
          <div className="receiving-empty-state">
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="TradeMagic"
              style={{ width: 64, height: 64 }}
            />
            <div className="receiving-empty-state-title">
              No inbound trades yet...
            </div>
            <div className="receiving-empty-state-message">
              The more cards you add to your want list, the more trades you will get.
            </div>
            <div className="receiving-empty-state-button">
              <MagicButton
                label="Add Cards to Want List"
                icon="heart"
                color="purple"
                onPress={() => navigate('/my-cards')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}